import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Fade from "@mui/material/Fade";

import Button from "../Button";
import StyledListItemIcon from "./StyledListItemIcon";

const StyledList = styled(List)({
  margin: 0,
  padding: 0,
});

const CollapsibleListItem = ({ children, title, icon, nodeId, isExpandable, onClick, forceExpanded = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(forceExpanded);
  const [userControlled, setUserControlled] = useState(false);

  // Update expanded state when forceExpanded changes (for search)
  useEffect(() => {
    if (!userControlled && forceExpanded !== undefined) {
      setExpanded(forceExpanded);
    }
  }, [forceExpanded, userControlled]);

  return (
    <Fade in={true} timeout={300}>
      <Box>
        <ListItemButton
          key={nodeId}
          onClick={() => {
            onClick && onClick();
          }}
          sx={{
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
            ...(forceExpanded && {
              animation: "pulse 0.5s ease-out",
              "@keyframes pulse": {
                "0%": {
                  backgroundColor: "transparent",
                },
                "50%": {
                  backgroundColor: (theme) => theme.palette.primary.light + "20",
                },
                "100%": {
                  backgroundColor: "transparent",
                },
              },
            }),
          }}
        >
          <StyledListItemIcon>{icon}</StyledListItemIcon>
          <ListItemText>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span>{title}</span>
              {isExpandable ? (
                <Button
                  title={expanded ? t("show_less") : t("show_more")}
                  icon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserControlled(true);
                    setExpanded(!expanded);
                  }}
                />
              ) : null}
            </Box>
          </ListItemText>
        </ListItemButton>
        {isExpandable ? (
          <Collapse key={`${nodeId}/col`} in={expanded} timeout="auto" unmountOnExit style={{ marginLeft: "2rem" }}>
            <StyledList dense>{children}</StyledList>
          </Collapse>
        ) : null}
      </Box>
    </Fade>
  );
};

export default CollapsibleListItem;
